import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { getTelegram, haptic } from '../lib/telegram';
import { Button } from './Button';

interface Props {
  productCode: string;
  fixtureId?: number;
  /** For PAID_LEAGUE — carried through to the bot worker's successful_payment handler. */
  leagueSlug?: string;
  label: string;
  stars: number;
  variant?: 'gold' | 'primary';
  fullWidth?: boolean;
  onSuccess?: () => void;
  invalidateKeys?: (string | number)[][];
}

export function StarsButton({
  productCode,
  fixtureId,
  leagueSlug,
  label,
  stars,
  variant = 'gold',
  fullWidth,
  onSuccess,
  invalidateKeys,
}: Props) {
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  // Refetch credit so the button label reflects whether the user is about to
  // spend Stars or in-app credit. Cheap query; cached for 30s.
  const credit = useQuery({
    queryKey: ['credit'],
    queryFn: () => api.myCredit(),
    staleTime: 30_000,
  });
  const creditCovers = (credit.data?.availableCreditStars ?? 0) >= stars;

  function flushInvalidations() {
    const flush = async () => {
      // Always also invalidate the credit query so the button label updates
      // for the next purchase intent.
      for (const key of [...(invalidateKeys ?? []), ['credit']]) {
        await qc.invalidateQueries({ queryKey: key });
      }
    };
    void flush();
    setTimeout(() => void flush(), 1500);
    setTimeout(() => void flush(), 4000);
  }

  async function buy() {
    setBusy(true);
    try {
      const res = await api.initiatePurchase(productCode, { fixtureId, leagueSlug });
      // Credit path: server already applied the product effect inline.
      if (res.paid && 'viaCredit' in res && res.viaCredit) {
        haptic.success();
        flushInvalidations();
        onSuccess?.();
        return;
      }
      // Stars-rail path: open the Telegram invoice as before.
      if ('invoiceLink' in res) {
        const tg = getTelegram();
        if (!tg) {
          haptic.error();
          alert('Stars purchases need Telegram — open this in the @PunditXIBot Mini App.');
          return;
        }
        tg.openInvoice(res.invoiceLink, (status) => {
          if (status === 'paid') {
            haptic.success();
            flushInvalidations();
            onSuccess?.();
          } else if (status === 'failed') {
            haptic.error();
          }
        });
      }
    } catch (e) {
      haptic.error();
      console.error('purchase initiate failed', e);
    } finally {
      setBusy(false);
    }
  }

  const buttonLabel = busy
    ? 'Opening…'
    : creditCovers
      ? `${label} · use ${stars} ⭐ credit`
      : `${label} · ${stars} ⭐`;

  return (
    <Button variant={variant} fullWidth={fullWidth} onClick={buy} disabled={busy}>
      {buttonLabel}
    </Button>
  );
}
